import React, { useEffect, useState } from 'react';
import { CopyButton, FieldController, RequiredValidator, LettersValidator, Checkbox, RadioButton, Anchor, Button, DropdownSearch, PanelHeader, TextField, NameValidator, useMatchScreenWidth } from '@keplerco/core';
import { FocusPanelLoaderLayer } from '../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { FetchType } from '../../../enums';
import { Company, Entity } from '../../../models';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions } from '../../../overmind';
import { ScoreComparison } from '../../../enums/score-comparison';
import { FlagIcon } from '../../../design/icons/flag.icon';
import { useForm } from 'react-hook-form';
import { Country } from '../../onboarding/classic/classic.onboarding.layout.models';

export function ManageCompanyPanel({ company, onCancel, onSubmit }: { company?: Company; onCancel: () => void; onSubmit: () => void }): React.ReactElement {
  const actions = useAppActions();

  const [industries, setIndustries] = useState<Entity[]>();
  const [countries, setCountries] = useState<Country[]>();

  const { control, handleSubmit } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const isMobile = useMatchScreenWidth('mobile');

  useEffect(() => {
    async function getIndustries() {
      const data = await actions.getIndustries();
      setIndustries(data);
    }

    async function getCountries() {
      const data = await actions.getCountries();
      setCountries(data);
    }

    async function getData() {
      actions.startLoader({ path: PagePath.userManagementSettings, type: FetchType.DialogFetching });
      await getIndustries();
      await getCountries();
      actions.stopLoader(PagePath.userManagementSettings);
    }

    getData();
  }, []);

  return (
    <FocusPanelLoaderLayer path={PagePath.userManagementSettings}>
      <div className="panelContent panelForm">
        {!!company ? (
          <PanelHeader title="Update company" type="actions" divider>
            <div className="headerActions" style={{ justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
              <CopyButton label="Company Id" value={company.companySlug} />
            </div>
          </PanelHeader>
        ) : (
          <PanelHeader title="Create company" divider />
        )}

        <form
          id="saveCompany"
          className="panelForm"
          onSubmit={handleSubmit(async fieldValues => {
            actions.startLoader({ path: PagePath.userManagementSettings, type: FetchType.Sending });

            const request = {
              ...fieldValues,
              companySlug: company?.companySlug,
              industryId: Number(fieldValues.industryId),
              countryIds: fieldValues.countryIds.split(',').map((countryId: string) => Number(countryId)),
            } as Company;
            console.log('company', request);
            const response = await actions.saveCompany(request);
            if (!!response?.companySlug) await actions.getCompanyVariables(response.companySlug);
            onSubmit();
            actions.stopLoader(PagePath.userManagementSettings);
          })}
        >
          <div>
            <h3 style={{ marginBottom: 12 }}>Setup company profile</h3>

            <FieldController
              control={control}
              name="companyName"
              rules={new NameValidator('Enter your company name')}
              defaultValue={company?.companyName}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Name"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter your company name',
                  }}
                />
              )}
            />
            <FieldController
              control={control}
              name="subDomain"
              rules={new LettersValidator('Enter your company sub domain')}
              defaultValue={company?.subDomain}
              render={({ field, fieldState }) => (
                <TextField
                  type="text"
                  {...field}
                  label="Sub domain"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter your company subdomain',
                  }}
                />
              )}
            />
            <FieldController
              control={control}
              name="industryId"
              rules={new RequiredValidator('Select your company industry')}
              defaultValue={company?.industryId}
              render={({ field, fieldState }) => (
                <DropdownSearch
                  {...field}
                  label="Industry"
                  options={(industries ?? []).map(industry => ({
                    value: industry.id!.toString(),
                    label: industry.name,
                  }))}
                  responsive
                  dialogContained
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    // invalid: !!fieldState.error, // TODO: why is fieldState.error not updating?
                    invalid: !field.value, // TODO: why is fieldState.error not updating?
                    message: fieldState.error?.message ?? 'Enter your company subdomain',
                  }}
                />
              )}
            />
            <FieldController
              control={control}
              name="countryIds"
              rules={new RequiredValidator('Select your company countries')}
              // defaultValue={companyToManage?.countryIds.join(',')} // TODO: why doesn't the defaultValue work for multiple?
              render={({ field, fieldState }) => (
                <DropdownSearch
                  {...field}
                  label="Countries"
                  defaultValue={company?.countryIds.join(',')} // TODO: why doesn't the defaultValue work for multiple?
                  options={(countries ?? []).map(country => ({
                    value: country.id!.toString(),
                    label: country.name,
                    icon: <FlagIcon isoCode={country.iso} />,
                  }))}
                  responsive
                  multiple
                  dialogContained
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    // invalid: !!fieldState.error, // TODO: why is fieldState.error not updating?
                    invalid: !field.value, // TODO: why is fieldState.error not updating?
                    message: fieldState.error?.message ?? 'Select your company countries',
                  }}
                />
              )}
            />

            <h3 style={{ borderTop: '1px solid var(--borders)', paddingTop: 12, marginBottom: 12 }}>Platform settings</h3>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
              <FieldController
                control={control}
                name="allowOffPlatformLearning"
                defaultValue={company?.allowOffPlatformLearning ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="allowOffPlatformLearning" clickablearea="label" checked={field.value}>
                    Allow off-platform learning
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowLessonReflections"
                defaultValue={company?.allowLessonReflections ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="allowLessonReflections" clickablearea="label" checked={field.value}>
                    Allow lesson reflections
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="includeSamlSSO"
                defaultValue={company?.includeSamlSSO ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="includeSamlSSO" clickablearea="label" checked={field.value}>
                    Include SAML SSO
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowCourseReflections"
                defaultValue={company?.allowCourseReflections ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="allowCourseReflections" clickablearea="label" checked={field.value}>
                    Allow course reflections
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="downloadLearningProgressPdf"
                defaultValue={company?.downloadLearningProgressPdf ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="downloadLearningProgressPdf" clickablearea="label" checked={field.value}>
                    Allow learner report download
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="displayJobInformationForm"
                defaultValue={company?.displayJobInformationForm ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="displayJobInformationForm" clickablearea="label" checked={field.value}>
                    Require job information
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="showPoweredByLogo"
                defaultValue={company?.showPoweredByLogo ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="showPoweredByLogo" clickablearea="label" checked={field.value}>
                    Display Kepler + Co logo
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowCreateAccount"
                defaultValue={company?.allowCreateAccount ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="allowCreateAccount" clickablearea="label" checked={field.value}>
                    Allow create account
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="useLevels"
                defaultValue={company?.useLevels ?? false}
                render={({ field }) => (
                  <Checkbox {...field} id="useLevels" clickablearea="label" checked={field.value}>
                    Use levels
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="sendNotificationEmails"
                defaultValue={company?.sendNotificationEmails ?? true}
                render={({ field }) => (
                  <Checkbox {...field} id="sendNotificationEmails" clickablearea="label" checked={field.value}>
                    Send notification emails
                  </Checkbox>
                )}
              />
            </div>

            <h3 style={{ borderTop: '1px solid var(--borders)', marginTop: 12, paddingTop: 12, marginBottom: 12 }}>Assessment settings</h3>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
              <FieldController
                control={control}
                name="allowGroupAssessment"
                defaultValue={company?.allowGroupAssessment ?? true}
                render={({ field }) => (
                  <Checkbox {...field} id="allowGroupAssessment" clickablearea="label" checked={field.value}>
                    Allow group assessment
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowRatingAssessment"
                defaultValue={company?.allowRatingAssessment ?? true}
                render={({ field }) => (
                  <Checkbox {...field} id="allowRatingAssessment" clickablearea="label" checked={field.value}>
                    Allow rating assessment
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowFOPAssessment"
                defaultValue={company?.allowFOPAssessment ?? true}
                render={({ field }) => (
                  <Checkbox {...field} id="allowFOPAssessment" clickablearea="label" checked={field.value}>
                    Allow field of practice assessments
                  </Checkbox>
                )}
              />
              <FieldController
                control={control}
                name="allowQuestionnaireAssessment"
                defaultValue={company?.allowQuestionnaireAssessment ?? true}
                render={({ field }) => (
                  <Checkbox {...field} id="allowQuestionnaireAssessment" clickablearea="label" checked={field.value}>
                    Allow questionnaire assessments
                  </Checkbox>
                )}
              />
            </div>

            <h3 style={{ borderTop: '1px solid var(--borders)', marginTop: 12, paddingTop: 12, marginBottom: 12 }}>Skill score comparison</h3>

            <FieldController
              control={control}
              name="scoreComparison"
              defaultValue={company?.scoreComparison ?? ScoreComparison.None}
              render={({ field }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <RadioButton {...field} id={ScoreComparison.Company} value={ScoreComparison.Company} clickablearea="label" checked={field.value === ScoreComparison.Company}>
                    Compare to Company Average
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.Department} value={ScoreComparison.Department} clickablearea="label" checked={field.value === ScoreComparison.Department}>
                    Compare to Department Average
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.Team} value={ScoreComparison.Team} clickablearea="label" checked={field.value === ScoreComparison.Team}>
                    Compare to Team Average
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.Role} value={ScoreComparison.Role} clickablearea="label" checked={field.value === ScoreComparison.Role}>
                    Compare to Role Average
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.FOPExpectedProficiency} value={ScoreComparison.FOPExpectedProficiency} clickablearea="label" checked={field.value === ScoreComparison.FOPExpectedProficiency}>
                    Compare to FOP Expected Proficiency
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.RoleExpectedProficiency} value={ScoreComparison.RoleExpectedProficiency} clickablearea="label" checked={field.value === ScoreComparison.RoleExpectedProficiency}>
                    Compare to Role Expected Proficiency
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.FieldOfPractice} value={ScoreComparison.FieldOfPractice} clickablearea="label" checked={field.value === ScoreComparison.FieldOfPractice}>
                    Compare to FOP Average
                  </RadioButton>

                  <RadioButton {...field} id={ScoreComparison.None} value={ScoreComparison.None} clickablearea="label" checked={field.value === ScoreComparison.None}>
                    None
                  </RadioButton>
                </div>
              )}
            />
          </div>

          <footer className="panelFooter" style={{ marginTop: 12 }}>
            <Anchor onClick={onCancel}>Cancel</Anchor>

            <Button type="button">Submit</Button>
          </footer>
        </form>
      </div>
    </FocusPanelLoaderLayer>
  );
}
